import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { BehaviorSubject } from 'rxjs';
import { Store } from '@ngrx/store';

import { Amount, PaxAmount } from '@fcom/dapi';
import { LanguageService } from '@fcom/ui-translate';
import { AirCalendarList } from '@fcom/dapi/api/models';
import { LocalDate, isPresent } from '@fcom/core/utils';
import { GlobalBookingTripDates } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces';
import { ButtonTheme, ButtonSize, ButtonMode, ModalButtons, NotificationTheme } from '@fcom/ui-components';
import { BookingAppState } from '@fcom/common/interfaces/booking';

import { BookingWidgetGtmService } from '../../services/booking-widget-gtm.service';
import { BookingWidgetActions } from '../../store/';
import { mapPaxAmountToPaxBreakdown } from '../../utils/utils.pax-amount';

@Component({
  selector: 'fin-air-calendar',
  templateUrl: 'air-calendar.component.html',
  styleUrls: ['./air-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AirCalendarComponent {
  private languageService = inject(LanguageService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);

  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TripType = TripType;
  readonly ModalButtons = ModalButtons;

  readonly paxAmount = input<PaxAmount>();

  readonly travelDates = input<GlobalBookingTripDates>();

  readonly tripType = input<TripType>();

  readonly prices = input<AirCalendarList>();

  readonly isAward = input(false);

  readonly totalPoints = input<number>();

  readonly selectDates = output<{ departureDate: LocalDate; returnDate: LocalDate }>();

  readonly continue = output<string>();

  private readonly passengerTranslations = toSignal(
    this.languageService.translate<Record<string, string>>('passenger')
  );

  startingPrice$ = new BehaviorSubject<Amount>({ amount: '0', currencyCode: 'EUR' });
  continueDisabled$ = new BehaviorSubject<boolean>(true);
  readonly airCalendarOpen = signal(false);
  readonly NotificationTheme = NotificationTheme;

  protected readonly paxAmountBreakdown = computed(() => {
    const paxAmount = this.paxAmount();
    const translations = this.passengerTranslations();
    return paxAmount && translations ? mapPaxAmountToPaxBreakdown(paxAmount, translations) : '';
  });

  constructor() {
    effect(() => {
      const open = isPresent(this.prices());
      this.airCalendarOpen.set(open);
      if (open) {
        this.bookingWidgetGtmService.pageViewEvent('/price-matrix');
      }
    });
  }

  selectCalendarDates({ dates, price }: { dates: string[]; price?: Amount }): void {
    const departureDate = new LocalDate(dates[0]);
    const returnDate = dates[1] ? new LocalDate(dates[1]) : undefined;

    this.startingPrice$.next(price);
    this.selectDates.emit({ departureDate, returnDate });
    this.bookingWidgetGtmService.trackElementEvent(
      'price-matrix',
      `DEPARTURE: ${departureDate}, RETURN: ${returnDate}`
    );
    // TODO: This is a really simple solution for enabling/disabling search from the matrix, do we need a better one?s
    this.continueDisabled$.next(false);
  }

  navigateToBookingFlow(): void {
    this.continue.emit(this.startingPrice$.getValue().amount);
  }

  closeAirCalendar(): void {
    this.store$.dispatch(BookingWidgetActions.setAirCalendarPrices({ prices: undefined }));
  }
}
